import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import styled from "@mui/material/styles/styled";
import {
  getGenericRelationText,
  getQFAndRefinementsLines,
} from "fe-common/logic";
import { Suggestion } from "../hooks/useSuggestions";

import { findingUsesLevel } from "../useDataInputPanels";
import { DataInput, DataInputPanel } from "../../dataInputTypes";
import { ClinicalReasoningTooltip } from "../common/ClinicalReasoningTooltip";
import { RosebudColorMap } from "../rosebud/rosebudColorMap";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { MobileOnly } from "../util/mobileOnly";
import { DiseaseCategoryType } from "fe-common/graphql";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { MobileView } from "../../../util/aKaunGeneralUtil";
import { useScreenSize } from "../util/useScreenSize";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { ItemEditComponent } from "../panels/ItemEditComponent";
import { KahunPanelSubGroup } from "shared";
import { ClinicalReasoningIconDesktop } from "../common/clinicalReasoningIconDesktop";
import { ClinicalReasoningIconMobile } from "../common/clinicalReasoningIconMobile";
//region [[ Styles ]]

const PlanItemView = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  minHeight: "50px",
  padding: "15px",
  paddingTop: "0px",
  width: "100%",
}));

const FindingTitle = styled("div")(({ theme }) => ({
  marginRight: "7px",
  marginBottom: "4px",
  width: "auto",
  minWidth: "unset",
  fontSize: "16px",
  color: "black",
  display: "flex",
  alignItems: "center",
  [theme.breakpoints.down(MobileView)]: {
    cursor: "pointer",
  },
}));

const FindingWithQualifier = styled("div")({
  display: "flex",
  flexFlow: "column",
  marginRight: "5px",
});

const ClinicalReasoningArea = styled("div")(
  ({ padding }: { padding: string }) => ({
    paddingLeft: padding === "true" ? "10px" : "0px",
    justifyContent: "flex-end", // Align right
  }),
);

const Question = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
}));

const ExternalButtonWrapper = styled("div")({
  display: "flex",
  justifyContent: "flex-end",
  flex: 2, // Let it grow
});
const ValueArea = styled("div")(({ theme }) => ({
  display: "flex",
  flex: 1,
  justifyContent: "flex-end",
  alignItems: "center",
}));

const TitleContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "start",
  alignItems: "center",
  flex: 4, // Mark as main area and let it expand
}));

const MobileClinicalReasoning = styled(ClinicalReasoningTooltip)(
  ({ theme }) => ({
    marginBottom: "0px",
    padding: "8px",
    margin: "10px",
    marginLeft: "5px",
    [theme.breakpoints.down(MobileView)]: {
      padding: "0px",
      marginTop: "10px",
      margin: "0px",
    },
  }),
);

const Qualifier = styled("div")(({ theme }) => ({
  fontSize: "13px",
}));

const SectionNumber = styled("div")(({ theme }) => ({
  backgroundColor: "transparent",
  color: RosebudColorMap.Default,
  fontWeight: 500,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  fontSize: "12px",
  width: "30px",
  padding: "10px",
  marginRight: "5px",
  height: "30px",

  [theme.breakpoints.down(MobileView)]: {
    fontSize: "14px",
    marginRight: "0px",
  },
}));

export const HideClinicalReasoningIcon = styled(RemoveCircleOutlineIcon)({
  color: KahunColorMap.Accent,
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface PlanItemProps {
  className?: string;
  suggestion?: Suggestion;
  loading?: boolean;
  skipSuggestion?: () => void;
  onAddFinding?: (resp: any) => void;
  onRemoveFinding?: (id: string) => void;
  color?: string;
  showActions?: boolean;
  hideSelectComponent?: boolean;
  index: number;
  showInlineReasoning?: boolean;
  editable: boolean;
  onChange: (resp: DataInput) => void;
  externalButton: any;
  selectionValue?: DataInput;
  panel?: DataInputPanel;
}

//endregion [[ Props ]]

//region [[ Functions ]]
/*const noQualifiersByDisease = (byDisease): boolean =>
  !byDisease.suggestion?.qualifiers?.length;*/
const isRefinementSuggestion = (s: Suggestion): boolean =>
  !!s?.finding?.qualifiers?.length &&
  s.category !== DiseaseCategoryType.OutcomeTest;

//endregion [[ Functions ]]

export const PlanItem = React.memo(
  ({
    suggestion,
    onRemoveFinding,
    onAddFinding,
    skipSuggestion,
    hideSelectComponent,
    selectionValue,
    panel,
    ...props
  }: PlanItemProps) => {
    const [showClinicalReasoning, setShowClinicalReasoning] = useState(false);

    const { isMobile } = useScreenSize();

    const isProcedure =
      panel && panel.panelSubgroup.id === KahunPanelSubGroup.Procedures;

    const { findingTitle, findingQualifiersText } = useMemo(() => {
      let [findingTitle, findingQualifiersText] = getQFAndRefinementsLines(
        suggestion ? suggestion.finding : null,
      );
      const relationText = (isRefinementSuggestion(suggestion)
        ? getGenericRelationText(suggestion.finding)
        : ""
      ).toString();

      if (relationText && findingQualifiersText) {
        findingQualifiersText =
          relationText +
          ": " +
          findingQualifiersText
            .replace(new RegExp(relationText, "g"), "")
            .replace(" ,", ",");
      }

      return { findingTitle, relationText, findingQualifiersText };
    }, [suggestion]);

    const isItLab = useMemo(() => {
      return suggestion?.finding && findingUsesLevel(suggestion.finding);
    }, [suggestion]);

    const trimFirstWord = useCallback((finding) => {
      let spaceIdx = finding.indexOf(" ");
      finding = finding.substring(spaceIdx);
      return finding;
    }, []);

    const onShowClinicalReasoning = () => {
      if (!isMobile) return;
      evt({
        category: CATEGORY.PLAN,
        action: showClinicalReasoning
          ? ACTION.HIDE_CLINICAL_REASONING
          : ACTION.SHOW_CLINICAL_REASONING,
        label: suggestion.finding.name,
      });

      setShowClinicalReasoning(!showClinicalReasoning);
    };

    if (!suggestion?.finding || props.loading) return null;
    return (
      <PlanItemView className={props.className}>
        {(!isProcedure || !panel) && (
          <Question className={"question"}>
            <TitleContainer>
              {props.index && (
                <SectionNumber className={"section-number"}>
                  {props.index}
                </SectionNumber>
              )}
              <FindingTitle
                className={"finding-title"}
                onClick={
                  props.showInlineReasoning ? onShowClinicalReasoning : null
                }
              >
                <FindingWithQualifier>
                  {!isItLab ? findingTitle : trimFirstWord(findingTitle)}
                  <Qualifier>{findingQualifiersText}</Qualifier>
                </FindingWithQualifier>
                {props.showInlineReasoning && (
                  <MobileOnly>
                    <MobileOnly>
                      <ClinicalReasoningIconMobile
                        showClinicalReasoning={showClinicalReasoning}
                        onShowClinicalReasoning={onShowClinicalReasoning}
                      />
                    </MobileOnly>
                  </MobileOnly>
                )}
              </FindingTitle>

              <ClinicalReasoningIconDesktop suggestion={suggestion} />
            </TitleContainer>
            {props.editable && (
              <ValueArea>
                <ItemEditComponent
                  selectionValue={selectionValue}
                  qf={suggestion.finding}
                  panel={panel}
                  suggestion={suggestion}
                  onChange={(resp: DataInput) => {
                    props.onChange(resp);
                  }}
                />
              </ValueArea>
            )}
            {!props.editable && !!props.externalButton && (
              <ExternalButtonWrapper className={"external-wrapper"}>
                {props.externalButton}
              </ExternalButtonWrapper>
            )}
          </Question>
        )}

        <ClinicalReasoningArea padding={props.index ? "true" : "false"}>
          <MobileClinicalReasoning
            suggestion={suggestion}
            show={props.showInlineReasoning && showClinicalReasoning}
          />
        </ClinicalReasoningArea>
      </PlanItemView>
    );
  },
);
